// Legacy: helper methods duplicated across architect/modern/classic/minimal
// template components (formatEntryDate, formatLanguageLevels, slugify, splitTitle,
// getEntryImages). `data.categories[].entries[]` arriving here is already the
// normalized shape produced by `mapResumeToPortfolioData` (display_title /
// display_description / ResumeEntryFile / ResumeEntryLink are always present).

import type { PortfolioCategoryEntry } from '../../types';
import type { TemplateEntryFile, TemplateEntryLink } from './types';

const MONTH_NAMES = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

/** Read an ad-hoc string field off an entry/category without widening its type. */
export function getEntryField(
  entry: PortfolioCategoryEntry | null | undefined,
  key: string,
): string {
  if (!entry) {
    return '';
  }
  const value = (entry as Record<string, unknown>)[key];
  return typeof value === 'string' ? value : '';
}

export function getEntryFiles(entry: PortfolioCategoryEntry | null | undefined): TemplateEntryFile[] {
  if (!entry || !Array.isArray(entry.ResumeEntryFile)) {
    return [];
  }
  return entry.ResumeEntryFile as TemplateEntryFile[];
}

export function getEntryLinks(entry: PortfolioCategoryEntry | null | undefined): TemplateEntryLink[] {
  if (!entry || !Array.isArray(entry.ResumeEntryLink)) {
    return [];
  }
  return entry.ResumeEntryLink as TemplateEntryLink[];
}

export function isImageFile(file: TemplateEntryFile | null | undefined): boolean {
  if (!file) {
    return false;
  }
  if (file.file_type && file.file_type.includes('image')) {
    return true;
  }
  return Boolean(file.file_url && /\.(png|jpe?g|gif|webp)$/i.test(file.file_url));
}

export function getEntryImages(entry: PortfolioCategoryEntry | null | undefined): TemplateEntryFile[] {
  return getEntryFiles(entry).filter(isImageFile);
}

function formatDatePart(month: number | null | undefined, year: number | null | undefined): string {
  if (!month && !year) {
    return '';
  }
  if (month && month >= 1 && month <= 12 && year) {
    return `${MONTH_NAMES[month - 1] || ''} ${year}`;
  }
  if (month && month >= 1 && month <= 12) {
    return MONTH_NAMES[month - 1] || '';
  }
  return year ? String(year) : '';
}

/** Legacy `formatEntryDate` / `formatEducationDates`. */
export function formatEntryDate(entry: PortfolioCategoryEntry | null | undefined): string {
  if (!entry) {
    return '';
  }
  const start = formatDatePart(entry.start_date_month, entry.start_date_year);
  const end = entry.is_current ? 'Present' : formatDatePart(entry.end_date_month, entry.end_date_year);
  if (start && end) {
    return `${start} - ${end}`;
  }
  return start || end || '';
}

/** Legacy `formatLanguageLevels`. */
export function formatLanguageLevels(entry: PortfolioCategoryEntry | null | undefined): string {
  if (!entry) {
    return '';
  }
  const parts: string[] = [];
  const push = (key: string, label: string) => {
    const value = getEntryField(entry, key);
    if (value) {
      parts.push(`${label} ${value}`);
    }
  };
  push('listening_level', 'Listening');
  push('reading_level', 'Reading');
  push('spoken_interaction_level', 'Spoken interaction');
  push('spoken_production_level', 'Spoken production');
  push('writing_level', 'Writing');
  push('proficiency_level', 'Proficiency');
  push('normal_level', 'Level');
  return parts.join(' · ');
}

export function hasLanguageSkills(entry: PortfolioCategoryEntry | null | undefined): boolean {
  if (!entry) {
    return false;
  }
  return Boolean(
    getEntryField(entry, 'listening_level') ||
      getEntryField(entry, 'reading_level') ||
      getEntryField(entry, 'spoken_interaction_level') ||
      getEntryField(entry, 'spoken_production_level') ||
      getEntryField(entry, 'writing_level') ||
      getEntryField(entry, 'proficiency_level'),
  );
}

export function hasAnyLangSkills(entries: PortfolioCategoryEntry[] | null | undefined): boolean {
  if (!entries) {
    return false;
  }
  return entries.some(hasLanguageSkills);
}

/** Legacy `slugify` (anchor ids / category matching). */
export function slugify(value: string | null | undefined): string {
  if (!value) {
    return '';
  }
  return String(value)
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9 -]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-');
}

/** Legacy `splitTitle` / `getPrimaryRoleParts`: split "First Rest" for two-tone headings. */
export function splitTitle(title: string | null | undefined): { first: string; rest: string } {
  if (!title) {
    return { first: '', rest: '' };
  }
  const parts = title.trim().split(/\s+/);
  const first = parts.shift() || '';
  return { first, rest: parts.join(' ') };
}

/** Title-case a full name, e.g. legacy `name` getter. */
export function titleCaseName(name: string | null | undefined): string {
  if (!name) {
    return '';
  }
  return name
    .toLowerCase()
    .split(' ')
    .map((word) => (word ? word.charAt(0).toUpperCase() + word.slice(1) : word))
    .join(' ');
}

function isImageFileType(fileType: string | undefined): boolean {
  return (
    fileType === 'image/jpeg' ||
    fileType === 'image/png' ||
    fileType === 'jpg' ||
    fileType === 'png' ||
    fileType === 'jpeg'
  );
}

export { isImageFileType };
